import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Sparkles, Scale, Sprout, GraduationCap, Briefcase } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import overVisual from '@/assets/webp bestanden/41 - TwentyTwelvePhotography_Systemique.webp';

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

function Over() {
  const opleidingen = [
    "International Business and Management – Rijksuniversiteit Groningen",
    "Groepsdynamiek – Els van Steijn bij het SIOO",
    "Systeemdynamieken in Families – Bert Hellinger Instituut",
    "Systeemdynamieken in Organisaties – Bert Hellinger Instituut",
  ];

  const ervaring = [
    "14+ jaar ervaring in organisatieadvies",
    "20+ systemische trajecten",
    "Specialisatie in leiderschapsontwikkeling",
    "Expert in team- en organisatiedynamiek"
  ];
  
  return (
    <>
      <SEO 
        title="Marthe van der Molen - Systemisch coach Castricum & Noord-Holland | Systemique"
        description="Marthe van der Molen is systemisch coach in Castricum met werkgebied Noord-Holland. Familieopstellingen, organisatieopstellingen, teamcoaching en 1-op-1 coaching in Amsterdam, Haarlem, Zaanstreek en omgeving."
        keywords="Marthe van der Molen, systemisch coach Castricum, systemisch coach Noord-Holland, systemisch coach Amsterdam, systemische coaching Haarlem, familieopstelling Castricum, organisatieopstelling Amsterdam, systemisch werk Zaanstreek, coach Noord-Holland, systemische coaching Alkmaar, teamcoach Zaandam, familieopstelling Haarlem, organisatieopstelling Uitgeest, systemisch werk Nederland, coaching Beverwijk, systemische begeleiding Hoofddorp"
        canonical="https://systemique.nl/over"
      />
      
      {/* Structured Data - Person */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Marthe van der Molen",
        "jobTitle": "Systemisch Coach en Trainer",
        "description": "Marthe van der Molen is systemisch coach in Castricum met werkgebied Noord-Holland. Ze is gespecialiseerd in familieopstellingen, organisatieopstellingen en systemische teamcoaching en begeleidt leiders, teams en particulieren bij het zichtbaar maken van systemische dynamieken.",
        "url": "https://systemique.nl/over",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Castricum",
          "addressRegion": "Noord-Holland",
          "addressCountry": "NL"
        },
        "worksFor": {
          "@type": "Organization",
          "name": "Systemique",
          "url": "https://systemique.nl"
        },
        "knowsAbout": [
          "Systemisch werk",
          "Familieopstellingen",
          "Organisatieopstellingen",
          "Systemische coaching",
          "Teamcoaching",
          "Systemische dynamieken",
          "Onderstroom in organisaties",
          "Systemisch leiderschap",
          "Systemische interventies",
          "Opstellingenwerk",
          "Systemische wetmatigheden",
          "Organisatieontwikkeling",
          "Coaching",
          "Opleidingen"
        ],
        "sameAs": [
          "https://www.linkedin.com/in/marthe-van-der-molen"
        ]
      })}
      </script>
      
      {/* Structured Data - Organization */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Systemique",
        "url": "https://systemique.nl",
        "logo": "https://systemique.nl/logo.png",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Castricum",
          "addressRegion": "Noord-Holland",
          "addressCountry": "NL"
        },
        "founder": {
          "@type": "Person",
          "name": "Marthe van der Molen"
        },
        "sameAs": [
          "https://www.linkedin.com/in/marthe-van-der-molen"
        ],
        "areaServed": [
          "Amsterdam",
          "Haarlem",
          "Zaandam",
          "Hoofddorp",
          "Amstelveen",
          "Hilversum",
          "Alkmaar",
          "Beverwijk",
          "Heemskerk",
          "Castricum",
          "Uitgeest",
          "Heerhugowaard",
          "Hoorn",
          "Purmerend",
          "Zaanstreek",
          "Utrecht",
          "Noord-Holland"
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Diensten van Marthe van der Molen",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Familieopstellingen",
                "description": "Familieopstellingen in Castricum, Amsterdam, Haarlem en de regio",
                "areaServed": [
                  {"@type": "City", "name": "Amsterdam"},
                  {"@type": "City", "name": "Haarlem"},
                  {"@type": "City", "name": "Castricum"},
                  {"@type": "State", "name": "Noord-Holland"}
                ]
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Organisatieopstellingen",
                "description": "Organisatieopstellingen in Castricum, Amsterdam, Haarlem en de regio",
                "areaServed": [
                  {"@type": "City", "name": "Amsterdam"},
                  {"@type": "City", "name": "Haarlem"},
                  {"@type": "City", "name": "Castricum"},
                  {"@type": "State", "name": "Noord-Holland"}
                ]
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Opleidingen systemisch werk",
                "description": "Opleidingen en trainingen in systemisch werk in Castricum en de regio",
                "areaServed": [
                  {"@type": "City", "name": "Amsterdam"},
                  {"@type": "City", "name": "Haarlem"},
                  {"@type": "City", "name": "Castricum"},
                  {"@type": "State", "name": "Noord-Holland"}
                ]
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Systemische Coaching",
                "description": "Systemische coaching voor leiders, teams en particulieren",
                "areaServed": [
                  {"@type": "City", "name": "Amsterdam"},
                  {"@type": "City", "name": "Haarlem"},
                  {"@type": "City", "name": "Zaandam"},
                  {"@type": "City", "name": "Alkmaar"},
                  {"@type": "City", "name": "Castricum"},
                  {"@type": "State", "name": "Noord-Holland"}
                ]
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Teamcoaching Systemisch",
                "description": "Systemische teamcoaching voor organisaties in Noord-Holland",
                "areaServed": [
                  {"@type": "City", "name": "Amsterdam"},
                  {"@type": "City", "name": "Haarlem"},
                  {"@type": "City", "name": "Zaanstreek"},
                  {"@type": "State", "name": "Noord-Holland"}
                ]
              }
            }
          ]
        }
      })}
      </script>
      
      <div className="min-h-screen bg-[#F9EBEF]">
      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" aria-hidden="true" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Over <span className="italic font-light font-serif text-[#F9EBEF]">Marthe</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Systemisch coach in Castricum, werkgebied Noord-Holland
          </p>
        </div>
      </section>

        {/* Introductie met Foto Placeholder */}
        <section className="py-12 md:py-24 px-6 relative overflow-hidden bg-white">
          <GrainTexture opacity={0.15} />
          {/* Decoratief achtergrondelement */}
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] bg-[#FAEBEE] rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="grid md:grid-cols-12 gap-12 items-center">
              
              {/* Linkerkolom: Afbeelding & Decoratie (5 kolommen) */}
              <div className="md:col-span-5 relative order-2 md:order-1">
                <div className="group aspect-[3/4] relative rounded-[3rem] overflow-hidden shadow-2xl">
                  <OptimizedImage 
                    src={overVisual}
                    webpSrc={overVisual}
                    alt="Marthe van der Molen - Systemisch coach in Castricum voor organisatieopstellingen, teamcoaching systemisch en familieopstellingen"
                    className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    width={600}
                    height={900}
                  />
                </div>
              </div>
              
              {/* Rechterkolom: Tekst (7 kolommen) */}
              <div className="md:col-span-7 order-1 md:order-2">
                <span className="text-[#854C73] font-bold tracking-widest uppercase text-xs mb-4 block">Mijn Verhaal</span>
                <h2 className="font-serif text-4xl md:text-6xl text-[#854C73] mb-8 leading-tight">
                  <span className="font-light font-serif">Marthe</span> <br/>
                  <span className="italic text-[#854C73] text-3xl md:text-5xl font-light font-serif">van der Molen</span>
                </h2>
                
                <div className="prose prose-lg text-[#6b4912] font-light leading-relaxed space-y-6">
                  <p>
                    Als systemisch coach in Castricum begeleid ik leiders, teams en particulieren in Noord-Holland en verder. Vanuit Systemique werk ik met{' '}
                    <Link to="/familieopstellingen" className="text-[#854C73] underline hover:text-[#590D0D] transition-colors">
                      familieopstellingen
                    </Link>
                    , organisatieopstellingen, teamcoaching en{' '}
                    <Link to="/1-op-1" className="text-[#854C73] underline hover:text-[#590D0D] transition-colors">
                      1-op-1 coaching
                    </Link>
                    .
                  </p>
                  <p className="first-letter:text-5xl first-letter:font-serif first-letter:text-[#854C73] first-letter:float-left first-letter:mr-3 first-letter:mt-[-8px]">
                    Na mijn bedrijfskundige studie en enkele jaren werkervaring merkte ik dat ik als organisatieadviseur tekortschoot. Interventies waren niet écht raak; vaak veerde een organisatie toch weer terug naar de oude gewoonten en patronen.
                  </p>
                  <p>
                    Na verschillende omzwervingen kwam ik uit bij het systemisch werk. Ik volgde opleidingen bij Els van Steijn en het Bert Hellinger Instituut en hoefde niet verder te zoeken, niet nog harder te werken en vond eindelijk een duurzame knop om organisaties van binnenuit te laten veranderen.
                  </p>
                  <p>
                    De kennis en ervaring met familie- en organisatiesystemen combineer ik met mijn bedrijfskundige en veranderkundige achtergrond. Zo geef ik invulling aan mijn missie om organisaties tot een fijne plek te maken, waar vanuit ontspanning en plezier gewerkt mag worden.
                  </p>
                </div>
                <div className="mt-10">
                  <Link to="/contact">
                    <Button className="bg-[#854C73] text-white hover:bg-[#590D0D] shadow-xl px-8 py-3 h-auto text-base">
                      Laten we kennismaken <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
              </Button>
                  </Link>
                </div>
            </div>
          </div>
        </div>
      </section>

        {/* Waarden Sectie */}
        <section className="py-24 px-6 bg-[#FAEBEE] relative">
          <GrainTexture opacity={0.1} />
          <GeoPattern />
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16">
              <span className="text-[#854C73] font-bold tracking-widest uppercase text-xs mb-4 block">Visie</span>
              <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">Mijn aanpak en waarden</h2>
              <div className="w-24 h-1 bg-[#854C73]/20 mx-auto rounded-full mt-6"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
              <SpotlightCard className="p-10 bg-white h-full border-none shadow-xl flex flex-col text-center group hover:-translate-y-2 transition-transform duration-500">
                <div className="w-16 h-16 bg-[#F2EADF] rounded-full flex items-center justify-center mb-8 mx-auto text-[#854C73] group-hover:scale-110 transition-transform"><Sparkles size={32} strokeWidth={1.5} aria-hidden="true" /></div>
                <h3 className="font-serif text-xl font-bold uppercase tracking-widest text-[#854C73] mb-4">Autonomie</h3>
                <p className="text-[#6b4912] leading-relaxed font-light text-sm">Wanneer we werkelijk vrij zijn om dat te doen wat ons ingegeven wordt. Vanuit het systemisch perspectief geloof ik in de evolutionaire kracht die ons het juiste pad wijst.</p>
              </SpotlightCard>
              <SpotlightCard className="p-10 bg-white h-full border-none shadow-xl flex flex-col text-center group hover:-translate-y-2 transition-transform duration-500">
                <div className="w-16 h-16 bg-[#F2EADF] rounded-full flex items-center justify-center mb-8 mx-auto text-[#915e84] group-hover:scale-110 transition-transform"><Scale size={32} strokeWidth={1.5} aria-hidden="true" /></div>
                <h3 className="font-serif text-xl font-bold uppercase tracking-widest text-[#915e84] mb-4">Ethiek</h3>
                <p className="text-[#6b4912] leading-relaxed font-light text-sm">Ik kijk oordeelloos naar jouw vraagstuk en context, en ben strikt in wat we wel en niet systemisch bekijken. We werken aan wat in jouw invloedsfeer ligt.</p>
              </SpotlightCard>
              <SpotlightCard className="p-10 bg-white h-full border-none shadow-xl flex flex-col text-center group hover:-translate-y-2 transition-transform duration-500">
                <div className="w-16 h-16 bg-[#F2EADF] rounded-full flex items-center justify-center mb-8 mx-auto text-[#590D0D] group-hover:scale-110 transition-transform"><Sprout size={32} strokeWidth={1.5} aria-hidden="true" /></div>
                <h3 className="font-serif text-xl font-bold uppercase tracking-widest text-[#590D0D] mb-4">Ontwikkeling</h3>
                <p className="text-[#6b4912] leading-relaxed font-light text-sm">Ik draag graag bij aan jouw zelfbewustwording, zodat jij autonomer, vollediger en vrijer in het leven staat en van optimale waarde bent.</p>
              </SpotlightCard>
          </div>
        </div>
      </section>

        {/* Achtergrond & Opleidingen Sectie - Dark/Luxe Theme */}
        <section className="py-24 px-6 bg-[#854C73] text-[#F9EBEF] relative overflow-hidden">
          <GrainTexture opacity={0.2} />
          {/* Decoratieve lijnen */}
          <div className="absolute top-0 left-1/4 w-px h-full bg-[#F9EBEF]/10"></div>
          <div className="absolute top-0 right-1/4 w-px h-full bg-[#F9EBEF]/10"></div>
          
          <div className="container mx-auto max-w-6xl relative z-10">
            
            {/* Header Sectie */}
            <div className="text-center mb-20 max-w-3xl mx-auto">
              <h2 className="font-serif text-4xl md:text-5xl mb-6 text-[#F9EBEF]">Opleiding en ervaring</h2>
              <p className="text-[#F9EBEF]/80 text-lg font-light leading-relaxed">Een combinatie van academische kennis, systemische kennis, veel vlieguren en Friese nuchterheid.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-16 md:gap-24">
              {/* Opleidingen Kolom - Zonder Card, Met Tijdlijn */}
              <div>
                <h3 className="font-serif text-3xl md:text-4xl mb-8 md:mb-12 border-b border-[#F9EBEF]/20 pb-6 flex items-center gap-4">
                  <GraduationCap className="text-[#F9EBEF]/60" size={32} aria-hidden="true" />
                  Opleidingen
                </h3>
                <div className="space-y-8 relative">
                  {/* Verticale tijdlijnstreep */}
                  <div className="absolute left-[9px] top-2 bottom-2 w-px bg-[#F9EBEF]/20"></div>
                  
                  {opleidingen.map((item, i) => (
                    <div key={i} className="flex gap-6 group relative">
                      {/* Custom Timeline Dot */}
                      <div className="mt-2 w-[19px] h-[19px] rounded-full border-2 border-[#F9EBEF] bg-[#854C73] group-hover:bg-[#F9EBEF] transition-colors z-10 flex-shrink-0"></div>
                      <p className="font-light text-lg leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ervaring Kolom - Zonder Card, Met Tijdlijn */}
              <div>
                <h3 className="font-serif text-3xl md:text-4xl mb-8 md:mb-12 border-b border-[#F9EBEF]/20 pb-6 flex items-center gap-4">
                  <Briefcase className="text-[#F9EBEF]/60" size={32} aria-hidden="true" />
                  Ervaring
                </h3>
                <div className="space-y-8 relative">
                  {/* Verticale tijdlijnstreep */}
                  <div className="absolute left-[9px] top-2 bottom-2 w-px bg-[#F9EBEF]/20"></div>

                  {ervaring.map((item, i) => (
                    <div key={i} className="flex gap-6 group relative">
                      <div className="mt-2 w-[19px] h-[19px] rounded-full border-2 border-[#F9EBEF] bg-[#854C73] group-hover:bg-[#F9EBEF] transition-colors z-10 flex-shrink-0"></div>
                      <p className="font-light text-lg leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        </div>
    </>
  );
}

export default Over;
